import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { Google, Apple, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Signup = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const password = watch('password');

  const onSubmit = async (data) => {
    setError('');
    setLoading(true);
    
    try {
      // Simulate API call with 3-second delay
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const result = await signup(data);
      if (result.success) {
        // Navigate to onboarding after loader
        navigate('/onboarding');
      }
    } catch (err) {
      setError('Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logoUrl =
    'https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png';

  // Style for inputs without black border
  const cleanField = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
      },
      transition: 'all 0.3s ease',
      background: 'rgba(255,255,255,0.85)',
    },
  };

  return (
    <>
      {/* Animated Loader Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
        }}
        open={loading}
      >
        <Box sx={{ 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4 
        }}>
          {/* Animated Logo */}
          <Box sx={{
            position: 'relative',
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box
              component="img"
              src={logoUrl}
              alt="RTNZERO"
              sx={{
                width: 80,
                height: 80,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 0.8 },
                  '50%': { transform: 'scale(1.1)', opacity: 1 },
                  '100%': { transform: 'scale(1)', opacity: 0.8 },
                }
              }}
            />
            
            {/* Rotating Circle 1 */}
            <CircularProgress
              size={120}
              thickness={1}
              sx={{
                position: 'absolute',
                color: '#10b981',
                animation: 'rotate 1s linear infinite',
              }}
            />
            
            {/* Rotating Circle 2 (reverse) */}
            <CircularProgress
              size={100}
              thickness={1}
              variant="determinate"
              value={75}
              sx={{
                position: 'absolute',
                color: '#3b82f6',
                animation: 'rotate 0.8s linear infinite reverse',
              }}
            />
          </Box>

          {/* Loading Text with Dots Animation */}
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Creating Your Account
            </Typography>
            <Typography variant="body1" color="grey.300" sx={{ mb: 2 }}>
              Setting up your sustainable business profile
            </Typography>
            
            {/* Animated Dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
              {[0, 1, 2].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    animation: `bounce 1.4s infinite ${dot * 0.2}s`,
                    '@keyframes bounce': {
                      '0%, 60%, 100%': {
                        transform: 'translateY(0)',
                      },
                      '30%': {
                        transform: 'translateY(-15px)',
                      },
                    }
                  }}
                />
              ))}
            </Box>

            {/* Progress Bar */}
            <Box sx={{ width: 300, mt: 4 }}>
              <Box sx={{ 
                width: '100%', 
                height: 4, 
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  width: '0%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                  borderRadius: 2,
                  animation: 'progress 3s ease-in-out forwards',
                  '@keyframes progress': {
                    '0%': { width: '0%' },
                    '100%': { width: '100%' },
                  }
                }} />
              </Box>
              <Typography variant="caption" color="grey.400" sx={{ mt: 1, display: 'block' }}>
                Almost there...
              </Typography>
            </Box>
          </Box>

          {/* Loading Steps */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1.5,
            mt: 4,
            maxWidth: 400
          }}>
            {[
              { text: 'Creating account...', completed: true },
              { text: 'Setting up dashboard...', completed: true },
              { text: 'Configuring sustainability metrics...', completed: false },
              { text: 'Preparing onboarding...', completed: false },
            ].map((step, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  color: step.completed ? '#10b981' : 'grey.400',
                  animation: step.completed ? 'none' : `fadeIn ${0.5 + index * 0.3}s ease-in`,
                  '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' },
                  }
                }}
              >
                <Box sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: step.completed ? '#10b981' : 'transparent',
                  border: step.completed ? 'none' : '1px solid rgba(255,255,255,0.3)',
                }}>
                  {step.completed ? '✓' : index + 1}
                </Box>
                <Typography variant="body2">{step.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Backdrop>

      {/* Main Signup Form */}
      <Container
        maxWidth="lg"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#ffffff',
          animation: 'fadeIn 0.8s ease',
        }}
      >
        <Grid
          container
          spacing={0}
          sx={{
            width: '100%',
            maxWidth: 900,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              animation: 'slideUp 0.7s ease',
            }}
          >
            <Paper
              elevation={4}
              sx={{
                p: 4,
                width: '100%',
                maxWidth: 460,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.65)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
              }}
            >
              {/* LOGO + TITLE */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <img
                  src={logoUrl}
                  alt="RTNZERO"
                  style={{
                    width: 120,
                    marginBottom: 12,
                  }}
                />
                <Typography variant="h5" fontWeight="bold" color="black">
                  Join RTNZERO
                </Typography>
                <Typography variant="body2" color="gray">
                  Create your sustainability account
                </Typography>
              </Box>

              {/* ERROR */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* FORM */}
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* FULL NAME */}
                <TextField
                  fullWidth
                  label="Full Name"
                  {...register('fullName', {
                    required: 'Full name is required',
                    pattern: {
                      value: /^[A-Za-z]+(\s[A-Za-z]+)+$/,
                      message: 'Enter first and last name',
                    },
                  })}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  sx={cleanField}
                />

                {/* EMAIL */}
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={cleanField}
                />

                {/* PASSWORD */}
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Min 8 characters required' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Must include uppercase, lowercase & number',
                    },
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={cleanField}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* CONFIRM PASSWORD */}
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (v) => v === password || 'Passwords do not match',
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  sx={cleanField}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* SUBMIT BUTTON */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    background: 'black',
                    color: 'white',
                    '&:hover': {
                      background: '#333',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(0,0,0,0.7)',
                      color: 'rgba(255,255,255,0.7)',
                    }
                  }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="gray">
                    OR SIGN UP WITH
                  </Typography>
                </Divider>

                {/* SOCIAL LOGIN */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Google />}
                      disabled={loading}
                      sx={{
                        borderColor: 'transparent',
                        color: 'black',
                        background: 'rgba(255,255,255,0.85)',
                        '&:hover': { background: 'rgba(0,0,0,0.05)' },
                        '&.Mui-disabled': {
                          background: 'rgba(255,255,255,0.5)',
                          color: 'rgba(0,0,0,0.3)',
                        }
                      }}
                    >
                      Google
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Apple />}
                      disabled={loading}
                      sx={{
                        borderColor: 'transparent',
                        color: 'black',
                        background: 'rgba(255,255,255,0.85)',
                        '&:hover': { background: 'rgba(0,0,0,0.05)' },
                        '&.Mui-disabled': {
                          background: 'rgba(255,255,255,0.5)',
                          color: 'rgba(0,0,0,0.3)',
                        }
                      }}
                    >
                      Apple
                    </Button>
                  </Grid>
                </Grid>

                {/* SIGN IN LINK */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="gray">
                    Already have an account?{' '}
                    <Link to="/login" style={{ textDecoration: 'none' }}>
                      <Typography component="span" color="black" fontWeight="bold">
                        Sign In
                      </Typography>
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Paper>
          </Grid>
        </Grid>

        {/* KEYFRAME ANIMATIONS */}
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes slideUp {
              from { transform: translateY(25px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }

            @keyframes rotate {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @keyframes bounce {
              0%, 60%, 100% {
                transform: translateY(0);
              }
              30% {
                transform: translateY(-15px);
              }
            }

            @keyframes progress {
              0% { width: 0%; }
              100% { width: 100%; }
            }

            @keyframes pulse {
              0% { transform: scale(1); opacity: 0.8; }
              50% { transform: scale(1.1); opacity: 1; }
              100% { transform: scale(1); opacity: 0.8; }
            }
          `}
        </style>
      </Container>
    </>
  );
};

export default Signup;